import BigNumber from 'bignumber.js';

import { Token, ChainId, Tokens, LidoTokenList } from '../constants';
import ModalFunc from '@/utils/components/modal';

import { useContract, useGetContractByUserWeb3 } from '@/hooks/use-contract';
import { Config as EthereumGasOracleConfig } from '@/contracts/l1oracle';
import { Config as L2GasOracleConfig } from '@/contracts/l2gasoracle';
import {
    getBridgeSpenderByChainId,
    getLidoBridgeSpenderByChainId,
} from '@/contracts/utils';
import { isEthereum, isMetisChain } from '@/utils';
import user from '@/stores/user';
import useTokenBalance from './use-token-balance';
import { handleError } from '@/utils/error-handler';

interface ServerGasEstimate {
    l1: BigNumber;
    l2: BigNumber;
}

interface BridgeParams {
    chainId: number;
    method: string;
    params: any[];
    extends: any;
}

interface LidoBridgeParams {
    method: string;
    params: any[];
    extends: any;
}

export default function useBridge() {
    const getContract = useGetContractByUserWeb3();

    const { web3, chainId } = user;

    const bridgeContract = useContract(
        getBridgeSpenderByChainId(chainId)?.address,
        getBridgeSpenderByChainId(chainId)?.abi,
    );
    const lidoBridgeContract = useContract(
        getLidoBridgeSpenderByChainId(chainId)?.address,
        getLidoBridgeSpenderByChainId(chainId)?.abi,
    );

    const { balance: gasTokenBalance } = useTokenBalance('');

    async function getGasEstimateFromServer(): Promise<
        ServerGasEstimate | undefined
    > {
        // const api = BridgeCostApi[env];
        // try {
        //     const res = await axios.get(api);
        //     if (res && res.data && res.data.data) {
        //         return {
        //             l1: new BigNumber(res.data.data.l1),
        //             l2: new BigNumber(res.data.data.l2),
        //         };
        //     }
        // } catch (e) {
        //     console.error(api);
        //     console.error(e);
        //     Alert({
        //         type: 'error',
        //         text: 'gas estimate server error ',
        //     });
        // }

        return Promise.resolve({
            l1: new BigNumber(0.5).shiftedBy(18),
            l2: new BigNumber(0),
        });
    }

    async function getEthereumToMetisGasOracle(chainId: ChainId) {
        try {
            const config =
                EthereumGasOracleConfig[
                    chainId as ChainId.mainnet | ChainId.testnet
                ];
            const contract = await getContract(
                config.address,
                config.abi,
                false,
            );

            if (contract) {
                const minL2GasRes = await contract.minL2Gas();

                if (minL2GasRes) {
                    return new BigNumber(minL2GasRes.toString());
                }
            }
        } catch (e) {
            console.error('getL2Gas func error', e);
        }

        return undefined;
    }

    async function getEthereumOracleDiscount(chainId: ChainId) {
        try {
            const config =
                EthereumGasOracleConfig[
                    chainId as ChainId.mainnet | ChainId.testnet
                ];
            const contract = await getContract(
                config.address,
                config.abi,
                false,
            );

            if (contract) {
                const res = await contract.discount();

                if (res) {
                    return new BigNumber(res.toString());
                }
            }
        } catch (e) {
            console.error(e);
            console.error(`getEthereumOracleDiscount error`);
        }
        return undefined;
    }

    async function getMetisGasOracleMinGasLimit(chainId: ChainId) {
        try {
            const config =
                L2GasOracleConfig[
                    chainId as ChainId.metisMainnet | ChainId.metisTestnet
                ];
            const contract = await getContract(
                config.address,
                config.abi,
                false,
            );

            if (contract) {
                const minL2GasRes = await contract.minErc20BridgeCost();

                if (minL2GasRes) {
                    return new BigNumber(minL2GasRes.toString());
                }
            }
        } catch (e) {
            console.error(e);
            console.error('getMetisGasOracleMinGasLimit');
        }
        return undefined;
    }

    async function getMetisTransferAtLeaseTokenAmount(chainId: ChainId) {
        const estimateRes = await getGasEstimateFromServer();

        let l1gasEstimate;
        if (estimateRes) {
            l1gasEstimate = estimateRes.l1;
        }

        const minL1GasLimit = await getMetisGasOracleMinGasLimit(chainId);

        console.log(
            'estimateResFromServerL1',
            l1gasEstimate ? l1gasEstimate.toFixed() : 'null',
        );
        console.log(
            'minL1GasLimit',
            minL1GasLimit ? minL1GasLimit.toFixed() : 'null',
        );
        if (l1gasEstimate && minL1GasLimit) {
            const minGas =
                minL1GasLimit && l1gasEstimate.isGreaterThan(l1gasEstimate)
                    ? minL1GasLimit
                    : l1gasEstimate;

            return minGas;
        }

        return undefined;
    }

    async function getBridgeTokenParams(
        currentChainId: ChainId,
        toChainId: ChainId,
        receivingAddress: string,
        amount: BigNumber,
        tokenName: Token,
    ): Promise<BridgeParams | undefined> {
        if (user.address && currentChainId) {
            try {
                const isEthereumEth =
                    tokenName === Token.eth && isEthereum(currentChainId);
                if (isEthereum(currentChainId)) {
                    let tokenAddressFrom = '';
                    let tokenAddressTo = '';

                    if (!isEthereumEth) {
                        tokenAddressFrom = (Tokens[tokenName] as any)[
                            currentChainId
                        ].address;
                    }

                    if (isEthereum(currentChainId)) {
                        tokenAddressTo = (Tokens[tokenName] as any)[toChainId]
                            .address;
                    }
                    if (
                        !isEthereum(currentChainId) &&
                        (!tokenAddressFrom || !tokenAddressTo)
                    ) {
                        console.error(
                            `Token ${tokenName}, layer1:${tokenAddressFrom}, layer2: ${tokenAddressTo} address error`,
                        );
                    }
                    const estimateRes = await getGasEstimateFromServer();
                    const l2gasRes = await getEthereumToMetisGasOracle(
                        currentChainId,
                    );

                    console.log(
                        'estimateResFromServerL2',
                        estimateRes ? estimateRes.l2.toFixed() : 'null',
                    );
                    console.log(
                        'l2gasRes',
                        l2gasRes ? l2gasRes.toFixed() : 'null',
                    );
                    if (estimateRes && l2gasRes) {
                        const gasParams = l2gasRes.isGreaterThan(estimateRes.l2)
                            ? l2gasRes
                            : estimateRes.l2;
                        const discount = await getEthereumOracleDiscount(
                            currentChainId,
                        );
                        console.log(
                            'discount',
                            discount ? discount.toFixed() : 'null',
                        );
                        if (discount) {
                            if (tokenName === Token.eth) {
                                console.log(
                                    'eth value',
                                    amount
                                        .shiftedBy(18)
                                        .integerValue()
                                        .toFixed(),
                                );
                                const isOverBalance = gasParams
                                    .multipliedBy(discount)
                                    .plus(amount.shiftedBy(18))
                                    .isGreaterThan(
                                        gasTokenBalance.shiftedBy(18),
                                    );
                                return {
                                    chainId: currentChainId,
                                    method: 'depositETHToByChainId',
                                    params: [
                                        toChainId,
                                        receivingAddress,
                                        gasParams.toFixed(),
                                        '0x00',
                                    ],

                                    extends: {
                                        // value: (isOverBalance
                                        //     ? layer1EthBalance.shiftedBy(18)
                                        //     : gasParams
                                        //           .multipliedBy(discount)
                                        //           .plus(
                                        //               amount.shiftedBy(18),
                                        //           )
                                        // )
                                        //     .integerValue()
                                        //     .toFixed(),
                                        value: amount
                                            .shiftedBy(18)
                                            .integerValue()
                                            .toFixed(),
                                    },
                                };
                            } else {
                                const isLidoBridge =
                                    LidoTokenList.includes(tokenName);
                                if (isLidoBridge) {
                                    return {
                                        chainId: currentChainId,
                                        method: 'depositERC20To',
                                        params: [
                                            // L1 metis token address
                                            tokenAddressFrom,
                                            // L2 metis token address, enter any address is fine
                                            tokenAddressTo,
                                            receivingAddress,
                                            amount
                                                .shiftedBy(
                                                    (Tokens[tokenName] as any)[
                                                        currentChainId
                                                    ].decimals,
                                                )
                                                .toFixed(),
                                            gasParams.toFixed(),
                                            '0x',
                                        ],

                                        extends: {
                                            // value: gasParams
                                            //     .multipliedBy(discount)
                                            //     .integerValue()
                                            //     .toFixed(),
                                            value: '0',
                                        },
                                    };
                                } else {
                                    return {
                                        chainId: currentChainId,
                                        method: 'depositERC20ToByChainId',
                                        params: [
                                            toChainId,
                                            // L1 metis token address
                                            tokenAddressFrom,
                                            // L2 metis token address, enter any address is fine
                                            tokenAddressTo,
                                            receivingAddress,
                                            amount
                                                .shiftedBy(
                                                    (Tokens[tokenName] as any)[
                                                        currentChainId
                                                    ].decimals,
                                                )
                                                .toFixed(),
                                            gasParams.toFixed(),
                                            '0x',
                                        ],

                                        extends: {
                                            // value: gasParams
                                            //     .multipliedBy(discount)
                                            //     .integerValue()
                                            //     .toFixed(),
                                            value: '0',
                                        },
                                    };
                                }
                            }
                        }
                    }
                } else {
                    const minGas = await getMetisTransferAtLeaseTokenAmount(
                        currentChainId,
                    );

                    if (minGas) {
                        if (tokenName === Token.metis) {
                            return {
                                chainId: currentChainId,
                                method: 'withdrawMetisTo',
                                params: [
                                    receivingAddress,
                                    amount
                                        .shiftedBy(
                                            Tokens[tokenName][currentChainId]
                                                .decimals,
                                        )
                                        .toFixed(),
                                    '0',
                                    '0x',
                                ],

                                extends: {
                                    value: minGas.toFixed(),
                                },
                            };
                        } else {
                            return {
                                chainId: currentChainId,
                                method: 'withdrawTo',
                                params: [
                                    (Tokens[tokenName] as any)[currentChainId]
                                        .address,
                                    receivingAddress,
                                    amount
                                        .shiftedBy(
                                            (Tokens[tokenName] as any)[
                                                currentChainId
                                            ].decimals,
                                        )
                                        .toFixed(),
                                    '0',
                                    '0x',
                                ],

                                extends: {
                                    value: minGas.toFixed(),
                                },
                            };
                        }
                    }
                }
            } catch (e) {
                console.error(e);
                console.error('getBridgeTokenParams error');
                handleError(e);
            }
        }

        return undefined;
    }

    async function estimateBridgeTokenGas(
        currentChainId: ChainId,
        toChainId: ChainId,
        amount: BigNumber,
        tokenName: Token,
        params: BridgeParams,
    ) {
        if (!amount || amount.isLessThan(0)) {
            return {
                fee_num: 0,
                estimateRemainingMainCoin: new BigNumber(0),
            };
        }
        const contract = LidoTokenList.includes(tokenName)
            ? lidoBridgeContract
            : bridgeContract;
        if (user.address && params && currentChainId) {
            try {
                // make sure layer2 metis have enouge metis token to transfer
                // if not add this logic, estimateGas will throw burn amount exceeds balance error
                if (tokenName === Token.metis && isMetisChain(currentChainId)) {
                    const value = new BigNumber(params.extends.value).shiftedBy(
                        -18,
                    );

                    // l2 services fee is greater than l2 metis balance
                    // valve is services fee
                    if (value.isGreaterThan(gasTokenBalance)) {
                        return {
                            fee_num: 0,
                            estimateRemainingMainCoin: value,
                        };
                    } else if (
                        value
                            .plus(amount)
                            .plus(0.1)
                            .isGreaterThan(gasTokenBalance)
                    ) {
                        // max logic
                        params.params[1] = gasTokenBalance
                            .minus(value)
                            .shiftedBy(18)
                            .integerValue()
                            .toFixed();
                    }
                }

                let transFee = 0;

                if (contract) {
                    const res = await contract[params.method].estimateGas(
                        ...(params.params as any),
                        {
                            from: user.address,
                            value: params.extends.value,
                        },
                    );
                    //here
                    if (res) {
                        const gasPrice = await user.web3.eth.getGasPrice();

                        transFee = new BigNumber(Number(res))
                            .multipliedBy(Number(gasPrice))
                            .shiftedBy(-18)
                            .toNumber();
                    }
                }
                let estimateRemainingMainCoin = new BigNumber(
                    params.extends.value,
                )
                    .shiftedBy(-18)
                    .plus(transFee);

                if (isEthereum(currentChainId) && tokenName == Token.eth) {
                    estimateRemainingMainCoin =
                        estimateRemainingMainCoin.minus(amount);
                }
                return {
                    fee_num: transFee,
                    // layer1 transfer use eth value to transfer, services fee = 0
                    servicesFee:
                        isEthereum(currentChainId) && tokenName === Token.eth
                            ? 0
                            : params.extends.value
                            ? new BigNumber(params.extends.value)
                                  .shiftedBy(-18)
                                  .toNumber()
                            : 0,
                    estimateRemainingMainCoin,
                };
            } catch (e) {
                console.error('estimateBridgeTokenGas error');
                console.error(e);
            }
        }

        return undefined;
    }

    async function bridgeToken(
        currentChainId: ChainId,
        toChainId: ChainId,
        receivingAddress: string,
        amount: BigNumber,
        tokenName: Token,
    ) {
        if (user.address && currentChainId) {
            const params = await getBridgeTokenParams(
                currentChainId,
                toChainId,
                receivingAddress,
                amount,
                tokenName,
            );

            const contract = LidoTokenList.includes(tokenName)
                ? lidoBridgeContract
                : bridgeContract;
            console.log('bridgeParams');
            console.log(params);
            // gas logic

            if (params) {
                try {
                    const estimateGas = await estimateBridgeTokenGas(
                        currentChainId,
                        toChainId,
                        amount,
                        tokenName,
                        params,
                    );

                    console.log('estimateGas', estimateGas);

                    if (isEthereum(currentChainId)) {
                        if (
                            (estimateGas &&
                                estimateGas.estimateRemainingMainCoin.toNumber() &&
                                estimateGas.estimateRemainingMainCoin
                                    .plus(tokenName === Token.eth ? amount : 0)
                                    .isGreaterThan(gasTokenBalance)) ||
                            gasTokenBalance
                                .shiftedBy(18)
                                .isLessThan(params.extends.value)
                        ) {
                            ModalFunc({
                                type: 'sorry',
                                title: 'Sorry',
                                text: `The current ETH Token balance ${gasTokenBalance.toFixed(
                                    3,
                                )} on your account is not sufficient to support this execution. Please leave at least ${(estimateGas
                                    ? estimateGas.estimateRemainingMainCoin.plus(
                                          estimateGas.fee_num,
                                      )
                                    : new BigNumber(
                                          params.extends.value,
                                      ).shiftedBy(-18)
                                ).toFixed(6)} ETH to support this execution`,
                                buttonText: 'OK',
                            });
                            return false;
                        }
                    } else {
                        if (
                            (estimateGas &&
                                estimateGas.estimateRemainingMainCoin.toNumber() &&
                                estimateGas.estimateRemainingMainCoin
                                    .plus(
                                        tokenName === Token.metis ? amount : 0,
                                    )
                                    .isGreaterThan(gasTokenBalance)) ||
                            gasTokenBalance
                                .shiftedBy(18)
                                .isLessThan(params.extends.value)
                        ) {
                            ModalFunc({
                                type: 'sorry',
                                title: 'Sorry',
                                text: `The current Metis Token balance ${gasTokenBalance.toFixed(
                                    3,
                                )} on your account is not sufficient to support this execution. Please leave at least ${(estimateGas
                                    ? estimateGas.estimateRemainingMainCoin.plus(
                                          estimateGas.fee_num,
                                      )
                                    : new BigNumber(
                                          params.extends.value,
                                      ).shiftedBy(-18)
                                ).toFixed(6)} METIS to support this execution`,
                                buttonText: 'OK',
                            });
                            return false;
                        }
                    }

                    if (contract) {
                        console.log('params', params.params);

                        const tx = await contract[params.method](
                            ...params.params,
                            {
                                value: params.extends.value,
                                gasLimit: isEthereum(currentChainId)
                                    ? 400000
                                    : 2000000,
                            },
                        );

                        const receipt = await tx.wait();
                        return receipt.hash;
                    }
                } catch (e) {
                    console.error(e);
                    console.error('bridgeToken error');
                    handleError(e);
                }
            } else {
                console.log('getBridgeTokenParams empty error');
            }
        }

        return false;
    }

    async function getBlockNumberFromHash(
        currentChainId: ChainId | undefined,
        tx: string,
    ) {
        try {
            if (user.web3) {
                const res = await user.web3.eth.getTransaction(tx);
                if (res && res.blockNumber) {
                    return Number(res.blockNumber);
                }
            }
        } catch (e) {
            console.error(e);
            console.error(
                `getBlockNumberFromHash: ${currentChainId} ${tx} ERROR`,
            );
        }
        return undefined;
    }

    async function getCurrnetBlockNumber(currentChainId: ChainId) {
        try {
            const res = await user.web3.eth.getBlockNumber();
            if (res) {
                return Number(res);
            }
        } catch (e) {
            console.error(e);
            console.error('getCurrnetBlockNumber error');
        }

        return undefined;
    }

    return {
        bridgeToken,
        getBlockNumberFromHash,
        getCurrnetBlockNumber,
        getMetisGasOracleMinGasLimit,
        getMetisTransferAtLeaseTokenAmount,
        getBridgeTokenParams,
        estimateBridgeTokenGas,
    };
}
